



















































































































































































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import ActivityStatusDisplay from '@/components/Activities/Display/ActivityStatus.vue';
import ActivityDto from '@/models/Activities/ActivityDto';
import ActivityType, { ActivityTypeLabels, ActivityTypeIcons } from '@/models/Activities/ActivityType';
import ActivityStatus from '@/models/Activities/ActivityStatus';
import ActivityDirection from '@/models/Activities/ActivityDirection';
import ActivityEntityType from '@/models/Activities/ActivityEntityType';
import { CallOutcomeLabels } from '@/models/Activities/CallOutcome';
import StatusDisplayLocation from '@/models/Shared/StatusDisplayLocation';
import { AppModule } from '@/store/modules/app';
import ajax from '@/lib/ajax';

@Component({
    name: 'ActivityDetailPane',
    components: { ActivityStatusDisplay }
})
export default class ActivityDetailPane extends GiganautVue {
    @Prop({ default: null })
    activity!: ActivityDto | null;

    @Prop({ default: 'activity' })
    emptyStateLabel!: string;

    // When true (terminal-state opportunity), all mutating actions are hidden —
    // the timeline becomes read-only.
    @Prop({ default: false })
    locked!: boolean;

    StatusDisplayLocation = StatusDisplayLocation;

    emailBodyHtml = '';
    loadingEmailBody = false;

    get typeLabel(): string {
        if (!this.activity) return '';
        const type = this.activity.type;
        const direction = this.activity.direction;
        if (type === ActivityType.Email) {
            if (direction === 2) return 'Inbound Email';
            if (direction === 1) return 'Outbound Email';
        }
        if (type === ActivityType.Call) {
            if (direction === 2) return 'Inbound Call';
            if (direction === 1) return 'Outbound Call';
        }
        return ActivityTypeLabels[type] || 'Activity';
    }

    get typeIcon(): string {
        if (!this.activity) return '';
        const type = this.activity.type;
        const direction = this.activity.direction;
        if (type === ActivityType.Email) {
            if (direction === 2) return this.IconConstants.EmailIncoming;
            if (direction === 1) return this.IconConstants.EmailOutgoing;
            return this.IconConstants.Email;
        }
        if (type === ActivityType.Call) {
            if (direction === 2) return this.IconConstants.PhoneIncoming;
            if (direction === 1) return this.IconConstants.PhoneOutgoing;
            return this.IconConstants.PhoneCall;
        }
        return ActivityTypeIcons[type] || this.IconConstants.Note;
    }

    get isNote(): boolean {
        return this.activity?.type === ActivityType.Note;
    }

    get isTask(): boolean {
        return this.activity?.type === ActivityType.Task;
    }

    get isCall(): boolean {
        return this.activity?.type === ActivityType.Call;
    }

    get isMeeting(): boolean {
        return this.activity?.type === ActivityType.Meeting;
    }

    get isEmail(): boolean {
        return this.activity?.type === ActivityType.Email;
    }

    get isOutboundEmail(): boolean {
        return this.isEmail && this.activity?.direction === ActivityDirection.Outbound;
    }

    get isDraftOutboundEmail(): boolean {
        return this.isOutboundEmail && this.activity?.status === ActivityStatus.Draft;
    }

    get isInboundEmail(): boolean {
        return this.isEmail && this.activity?.direction === ActivityDirection.Inbound;
    }

    get isCompleted(): boolean {
        return this.activity?.status === ActivityStatus.Completed;
    }

    get isOverdue(): boolean {
        if (!this.activity?.endAt || this.isCompleted) return false;
        return new Date(this.activity.endAt) < new Date();
    }

    get hasOwner(): boolean {
        return this.activity?.owner != null;
    }

    get ownerName(): string {
        if (!this.activity?.owner) return '';
        const person = this.activity.owner.person;
        return person ? `${person.firstName} ${person.lastName}` : '';
    }

    get ownerInitials(): string {
        if (!this.activity?.owner?.person) return '';
        const first = this.activity.owner.person.firstName ? this.activity.owner.person.firstName.charAt(0).toUpperCase() : '';
        const last = this.activity.owner.person.lastName ? this.activity.owner.person.lastName.charAt(0).toUpperCase() : '';
        return first + last;
    }

    get ownerAvatarUrl(): string {
        if (!this.activity?.owner?.avatar) return '';
        return AppModule.currentAgencyContext.storageSharedBaseUrl + '/avatars/2/' + this.activity.owner.id + '/resized-100/' + this.activity.owner.avatar;
    }

    get createdByAvatarUrl(): string {
        if (!this.activity?.createdBy?.avatar) return '';
        return AppModule.currentAgencyContext.storageSharedBaseUrl + '/avatars/2/' + this.activity.createdBy.id + '/resized-100/' + this.activity.createdBy.avatar;
    }

    get createdByInitials(): string {
        if (!this.activity?.createdBy) return '';
        const first = this.activity.createdBy.firstName ? this.activity.createdBy.firstName.charAt(0).toUpperCase() : '';
        const last = this.activity.createdBy.lastName ? this.activity.createdBy.lastName.charAt(0).toUpperCase() : '';
        return first + last;
    }

    get directionLabel(): string {
        if (!this.activity?.direction) return '-';
        return this.activity.direction === ActivityDirection.Outbound ? 'Outbound' : 'Inbound';
    }

    get callOutcomeLabel(): string {
        if (!this.activity?.callOutcome) return '-';
        return CallOutcomeLabels[this.activity.callOutcome] || '-';
    }

    get relatedEntityIcon(): string {
        if (!this.activity) return '';
        switch (this.activity.primaryEntityType) {
            case ActivityEntityType.Contact: return this.IconConstants.Contact;
            case ActivityEntityType.Worker: return this.IconConstants.Worker;
            case ActivityEntityType.Company: return this.IconConstants.Company;
            case ActivityEntityType.Location: return this.IconConstants.Location;
            case ActivityEntityType.Job: return this.IconConstants.Job;
            case ActivityEntityType.Booking: return this.IconConstants.Booking;
            case ActivityEntityType.Opportunity: return this.IconConstants.Opportunity;
            default: return this.IconConstants.Activity;
        }
    }

    get relatedEntityPath(): string {
        if (!this.activity) return '';
        switch (this.activity.primaryEntityType) {
            case ActivityEntityType.Contact: return 'contacts';
            case ActivityEntityType.Worker: return 'workers';
            case ActivityEntityType.Company: return 'companies';
            case ActivityEntityType.Location: return 'locations';
            case ActivityEntityType.Job: return 'jobs';
            case ActivityEntityType.Booking: return 'bookings';
            case ActivityEntityType.Opportunity: return 'opportunities';
            default: return '';
        }
    }

    get relatedEntityRoute(): string {
        if (!this.activity || !this.activity.primaryEntityId) return '';
        return '/agency/' + this.relatedEntityPath + '/' + this.activity.primaryEntityId;
    }

    get hasRelatedEntity(): boolean {
        if (!this.activity) return false;
        return !!(this.activity.contact || this.activity.worker || this.activity.company || this.activity.location || this.activity.primaryEntityName);
    }

    @Watch('activity')
    onActivityChanged() {
        this.emailBodyHtml = '';
        if (this.activity && this.isEmail) {
            this.loadEmailBody();
        }
    }

    async loadEmailBody() {
        if (!this.activity || !this.activity.emailBodyStoragePath) {
            this.emailBodyHtml = '';
            return;
        }

        this.loadingEmailBody = true;
        try {
            const response = await ajax.get(`/api/v1/agency/activities/emails/${this.activity.id}/body?content=true`);
            this.emailBodyHtml = response.data.content || '';
        } catch (error) {
            this.emailBodyHtml = '';
        } finally {
            this.loadingEmailBody = false;
        }
    }

    resizeIframe() {
        const iframe = this.$refs.emailIframe as HTMLIFrameElement;
        if (!iframe || !iframe.contentDocument || !iframe.contentDocument.body) return;
        const body = iframe.contentDocument.body;
        // Remove default body margin and hide overflow inside iframe
        body.style.margin = '0';
        body.style.overflow = 'hidden';
        const measure = () => {
            // Clamp to a minimum: scrollHeight can read 0 when @load fires before
            // the srcdoc content has laid out (common for short bodies like a
            // single <p>), which would otherwise collapse the frame and hide the
            // email entirely.
            iframe.style.height = Math.max(body.scrollHeight, 40) + 'px';
        };
        measure();
        requestAnimationFrame(measure);
    }

    openEntityInNewWindow(entityPath: string, entityId: string) {
        window.open(window.location.origin + '/agency/' + entityPath + '/' + entityId);
    }

    async downloadAttachment(attachment: { id: string; filename: string }) {
        try {
            const response = await ajax.get(`/api/v1/agency/activities/emails/attachments/${attachment.id}`);
            if (response.data && response.data.signedUrl) {
                window.open(response.data.signedUrl, '_blank');
            }
        } catch (error) {
            AppModule.handleApiError(error);
        }
    }

}
